import { PinchGesture } from '@use-gesture/vanilla';
import { renderFilterButtons } from './filter-ui.js';
import { resetCrumbTracking } from './gallery-breadcrumb.js';
import { showShare, hideShare } from './share.js';

// ── Layout ────────────────────────────────────────────────────────────────────
const ITEM_W     = 260;
const ITEM_H     = 195;   // 16:9 fallback
const COLS       = 6;
const COL_GAP    = 80;
const ROW_GAP    = 50;
const ROW_JITTER = 60;

// ── Behaviour ─────────────────────────────────────────────────────────────────
const ZOOM_MIN      = 0.05;
const ZOOM_MAX      = 4;
const ZOOM_LIGHTBOX = 1.8;
const MOUNT_BATCH   = 20;
const ZOOM_VEL_MAX  = 0.12;

function jitter(seed, range) {
    const x = Math.sin(seed * 9301 + 49297) * 233280;
    return ((x - Math.floor(x)) - 0.5) * range;
}

class VideoWall {
    constructor() {
        this.viewport = document.getElementById('video-viewport');
        this.world    = document.getElementById('video-world');
        if (!this.viewport || !this.world) return;

        this.modal       = document.getElementById('video-modal');
        this.modalIframe = document.getElementById('video-modal-iframe');
        this.modalTitle  = document.getElementById('video-modal-title');

        this.data          = [];
        this.items         = [];
        this.worldW        = 0;
        this.worldH        = 0;
        this.zoom          = 1;
        this.panX          = 0;
        this.panY          = 0;
        this.velX          = 0;
        this.velY          = 0;
        this.W             = window.innerWidth;
        this.H             = window.innerHeight;
        this.zoomVel       = 0;
        this.zoomCX        = 0;
        this.zoomCY        = 0;
        this.ZOOM_LEVELS   = [];
        this._raf          = null;
        this._dragging     = false;
        this._lastPinchScale = null;
        this._activeFilter = 'all';
        this._filterCrumb  = '';
        this._crumbItem    = null;
        this._crumbInitialised = false;
        this._crumbTrackingStarted = false;
        this._crumbVisible = false;

        this.init();
    }

    destroy() {
        if (this._raf !== null) { cancelAnimationFrame(this._raf); this._raf = null; }
        if (this._onResize) window.removeEventListener('resize', this._onResize);
        if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    }

    async init() {
        this._onResize = () => this.onResize();
        window.addEventListener('resize', this._onResize);
        await this.loadData();
        this.buildLayout();
        this._renderFilterButtons();
        this.bindEvents();
        this._wake();
        this.checkUrlParam();
    }

    onResize() {
        this.W = window.innerWidth;
        this.H = window.innerHeight;
        if (this.worldH > 0) this._syncWorld();
    }

    async loadData() {
        try {
            const res = await fetch(
                `${window.JK.pbUrl}/api/collections/videos/records?` +
                new URLSearchParams({ perPage: 500, sort: 'collection,title' })
            );
            const json = await res.json();
            this.data = json.items || [];
        } catch (err) {
            console.error('VideoWall: load failed', err);
        }
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    buildLayout() {
        for (const item of this.items) this._unmountItem(item);

        const pool = this._activeFilter === 'all'
            ? [...this.data]
            : this.data.filter(v => v.collection === this._activeFilter);

        // Stable seeded shuffle
        pool.sort((a, b) => {
            const sa = Math.sin(a.id.charCodeAt(0) * 9301) * 233280;
            const sb = Math.sin(b.id.charCodeAt(0) * 9301) * 233280;
            return (sa - Math.floor(sa)) - (sb - Math.floor(sb));
        });

        const cols = Math.min(COLS, Math.max(1, pool.length));
        const colH = new Array(cols).fill(0);
        for (let c = 0; c < cols; c++) {
            colH[c] = jitter(c, ROW_JITTER * 2) + ROW_JITTER;
        }

        this.items = [];
        pool.forEach((item, idx) => {
            const col   = idx % cols;
            item._h          = ITEM_H;
            item._x          = col * (ITEM_W + COL_GAP) + jitter(idx, 20);
            item._y          = colH[col];
            item._el         = null;
            item._loadedTier = -1;
            this.items.push(item);
            colH[col] += item._h + ROW_GAP + Math.abs(jitter(idx + 100, ROW_JITTER));
        });

        this.worldW = cols * (ITEM_W + COL_GAP) - COL_GAP;
        this.worldH = Math.max(...colH, 1);
        this._zoomToFit();
    }

    _zoomToFit() {
        // Fit to viewport width so columns space evenly across the screen.
        // World scrolls vertically. Only called on initial load — resize
        // preserves the user's current zoom/pan.
        this.zoom = Math.min(this.W / this.worldW * 0.95, ZOOM_MAX);
        this.panX = (this.W - this.worldW * this.zoom) / 2;
        this.panY = 80;

        const fitZ = this.zoom;
        this.ZOOM_LEVELS = [
            fitZ,
            Math.min(fitZ * 4,  ZOOM_MAX),
            Math.min(fitZ * 10, ZOOM_MAX),
            Math.min(1.0,       ZOOM_MAX),
        ].filter((z, i, a) => i === 0 || z > a[i - 1] * 1.2);

        this._syncWorld();
    }

    // ── Filter ────────────────────────────────────────────────────────────────

    _itemVisible(item) {
        return this._activeFilter === 'all' || item.collection === this._activeFilter;
    }

    setFilter(filter) {
        this._activeFilter = filter;
        this._filterCrumb = filter !== 'all' ? filter : '';
        resetCrumbTracking(this);
        for (const item of this.items) {
            if (item._el) item._el.style.display = this._itemVisible(item) ? '' : 'none';
        }
        this._fitVisible();
        this._wake();
    }

    _fitVisible() {
        const visible = this.items.filter(i => this._itemVisible(i));
        if (!visible.length) return;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (const item of visible) {
            minX = Math.min(minX, item._x);
            minY = Math.min(minY, item._y);
            maxX = Math.max(maxX, item._x + ITEM_W);
            maxY = Math.max(maxY, item._y + item._h);
        }
        const pad     = 60;
        const targetZ = Math.min(this.W / (maxX - minX + pad * 2), this.H / (maxY - minY + pad * 2), ZOOM_MAX);
        const endX    = this.W / 2 - ((minX + maxX) / 2) * targetZ;
        const endY    = this.H / 2 - ((minY + maxY) / 2) * targetZ;

        const startZ = this.zoom, startX = this.panX, startY = this.panY;
        this.zoomVel = 0; this.velX = this.velY = 0;
        const startTime = performance.now();
        const tick = now => {
            const t = Math.min(1, (now - startTime) / 450);
            const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoom = startZ + (targetZ - startZ) * e;
            this.panX = startX + (endX - startX) * e;
            this.panY = startY + (endY - startY) * e;
            this._syncWorld();
            if (t < 1) requestAnimationFrame(tick);
            else this._wake();
        };
        requestAnimationFrame(tick);
    }

    _renderFilterButtons() {
        const seen = new Set();
        const groups = [];
        for (const item of this.data) {
            if (item.collection && !seen.has(item.collection)) {
                seen.add(item.collection);
                groups.push({ key: item.collection, label: item.collection });
            }
        }
        if (!groups.length) return;
        const nav = document.getElementById('video-filter');
        renderFilterButtons(nav, groups, this._activeFilter, key => {
            this.setFilter(key);
        });
    }

    // ── DOM virtualiser ───────────────────────────────────────────────────────

    updateScene() {
        const vl = -this.panX / this.zoom;
        const vt = -this.panY / this.zoom;
        const vr = vl + this.W / this.zoom;
        const vb = vt + this.H / this.zoom;
        const buf = ITEM_H;

        let mounted = 0, pending = false;

        for (const item of this.items) {
            const inView = item._x < vr      && item._x + ITEM_W > vl &&
                           item._y < vb + buf && item._y + item._h > vt - buf;
            if (inView) {
                if (!item._el) {
                    if (mounted >= MOUNT_BATCH) { pending = true; continue; }
                    this._mountItem(item);
                    item._el.style.display = this._itemVisible(item) ? '' : 'none';
                    mounted++;
                }
            } else if (item._el) {
                this._unmountItem(item);
            }
        }
        return pending;
    }

    _mountItem(item) {
        const el = document.createElement('div');
        el.className = 'video-wall-item';
        el.style.cssText = `left:${item._x}px;top:${item._y}px;width:${ITEM_W}px;height:${item._h}px`;

        const url = this.thumbUrl(item);
        if (url) el.style.backgroundImage = `url(${url})`;

        // If a hand-crafted short clip exists, lazy-load a muted/looping <video>
        // that plays when in the actual viewport (IntersectionObserver). Falls
        // back gracefully to the background image when no clip is set.
        if (item.short_clip && item.collectionId && item.id) {
            const v = document.createElement('video');
            v.className = 'video-wall-clip';
            v.muted = true;
            v.loop = true;
            v.playsInline = true;
            v.preload = 'none';
            v.dataset.src = `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.short_clip}`;
            el.appendChild(v);
            item._clipEl = v;
            this._observeClip(v);
        }

        const play = document.createElement('div');
        play.className = 'video-play-icon';
        play.textContent = 'PLAY';
        el.appendChild(play);

        item._el         = el;
        item._loadedTier = 0;
        this.world.appendChild(el);
    }

    _unmountItem(item) {
        if (item._clipEl) {
            this._unobserveClip(item._clipEl);
            try { item._clipEl.pause(); item._clipEl.removeAttribute('src'); item._clipEl.load(); } catch {}
            item._clipEl = null;
        }
        item._el?.remove();
        item._el         = null;
        item._loadedTier = -1;
    }

    _observeClip(v) {
        if (!this._clipObserver) {
            // Mobile: bound simultaneously-playing clips — each playing <video>
            // holds a hardware decoder + decoded frames; iOS gets unhappy with
            // many at once. Desktop is effectively unbounded.
            const maxPlaying = window.innerWidth < 768 ? 3 : 32;
            this._playingClips = new Set();
            this._clipObserver = new IntersectionObserver(entries => {
                for (const e of entries) {
                    const v = e.target;
                    if (e.isIntersecting) {
                        if (this._playingClips.size >= maxPlaying && !this._playingClips.has(v)) continue;
                        if (!v.src && v.dataset.src) v.src = v.dataset.src;
                        this._playingClips.add(v);
                        const p = v.play(); if (p && p.catch) p.catch(() => {});
                    } else {
                        v.pause();
                        this._playingClips.delete(v);
                    }
                }
            }, { rootMargin: '0px', threshold: 0.1 });
        }
        this._clipObserver.observe(v);
    }

    _unobserveClip(v) {
        this._clipObserver?.unobserve(v);
        this._playingClips?.delete(v);
    }

    thumbUrl(item) {
        if (!item.thumbnail || !item.collectionId || !item.id) return '';
        // Sized thumb instead of the raw poster (which can be 1920px+): nginx
        // serves pre-generated sizes from disk, PocketBase generates on miss.
        // Mobile decodes ~20× less image data at typical wall zoom.
        const size = window.innerWidth < 768 ? '300x0' : '800x0';
        return `${window.JK.pbUrl}/api/files/${item.collectionId}/${item.id}/${item.thumbnail}?thumb=${size}`;
    }

    // ── RAF loop ──────────────────────────────────────────────────────────────

    _tick() {
        this.applyInertia();
        const needsMore = this.updateScene();
        const center = this._centerItem();
        if (!this._crumbInitialised) {
            this._crumbItem = center;
            this._crumbInitialised = true;
        }
        if (this._crumbTrackingStarted && (!this._crumbVisible || center !== this._crumbItem)) {
            this._crumbItem = center;
            this.updateCrumb(center);
            this._crumbVisible = true;
        }
        if (this._raf !== null || needsMore) {
            this._raf = requestAnimationFrame(() => this._tick());
        }
    }

    _wake() {
        if (this._raf === null) {
            this._raf = requestAnimationFrame(() => this._tick());
        }
    }

    _maybeIdle() {
        if (Math.abs(this.velX) < 0.001 && Math.abs(this.velY) < 0.001 &&
            Math.abs(this.zoomVel) < 0.0005 && !this._dragging) {
            this._raf = null;
        }
    }

    // ── Events ────────────────────────────────────────────────────────────────

    bindEvents() {
        const vp = this.viewport;
        let hasDragged = false, dragStart = null, lastPos = null, lastTime = null;

        vp.addEventListener('pointerdown', e => {
            this._crumbTrackingStarted = true;
            vp.setPointerCapture(e.pointerId);
            this._dragging = true;
            hasDragged     = false;
            dragStart      = { x: e.clientX, y: e.clientY };
            lastPos        = { x: e.clientX, y: e.clientY };
            lastTime       = performance.now();
            this.velX = this.velY = 0;
            vp.classList.add('is-dragging');
            this._wake();
        });

        vp.addEventListener('pointermove', e => {
            if (!this._dragging) return;
            const dx = e.clientX - lastPos.x;
            const dy = e.clientY - lastPos.y;
            const dt = performance.now() - lastTime || 1;
            if (Math.abs(e.clientX - dragStart.x) > 4 || Math.abs(e.clientY - dragStart.y) > 4)
                hasDragged = true;
            this.panX += dx; this.panY += dy;
            this.velX  = dx / dt; this.velY = dy / dt;
            lastPos    = { x: e.clientX, y: e.clientY };
            lastTime   = performance.now();
            this._syncWorld();
        });

        vp.addEventListener('pointerup', e => {
            this._dragging = false;
            vp.classList.remove('is-dragging');
            if (!hasDragged) this.handleClick(e.clientX, e.clientY);
        });

        vp.addEventListener('pointercancel', () => {
            this._dragging = false;
            vp.classList.remove('is-dragging');
        });

        vp.addEventListener('wheel', e => {
            e.preventDefault();
            this._crumbTrackingStarted = true;
            const delta = e.deltaMode === 1 ? e.deltaY * 30 : e.deltaY;
            this.zoomVel = Math.max(-ZOOM_VEL_MAX, Math.min(ZOOM_VEL_MAX,
                this.zoomVel + (delta < 0 ? 0.07 : -0.07)));
            this.zoomCX = e.clientX;
            this.zoomCY = e.clientY;
            this._wake();
        }, { passive: false });

        new PinchGesture(vp, ({ origin: [ox, oy], offset: [s], last }) => {
            this._crumbTrackingStarted = true;
            const prev = this._lastPinchScale ?? s;
            if (s !== prev) this.zoomAt(ox, oy, s / prev);
            this._lastPinchScale = last ? null : s;
            this._wake();
        });

        document.getElementById('zoom-in') ?.addEventListener('click', () => {
            this._crumbTrackingStarted = true;
            this.zoomIn();
        });
        document.getElementById('zoom-out')?.addEventListener('click', () => {
            this._crumbTrackingStarted = true;
            this.zoomOut();
        });

        // Modal close
        this.modal?.addEventListener('click', e => {
            if (e.target === this.modal || e.target.closest('.video-modal-close')) this.closeModal();
        });
        this._onKeydown = e => { if (e.key === 'Escape') this.closeModal(); };
        document.addEventListener('keydown', this._onKeydown);
    }

    // ── Zoom / pan ────────────────────────────────────────────────────────────

    zoomAt(cx, cy, factor) {
        const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.zoom * factor));
        const ratio   = newZoom / this.zoom;
        this.panX = cx - ratio * (cx - this.panX);
        this.panY = cy - ratio * (cy - this.panY);
        this.zoom = newZoom;
        this._syncWorld();
    }

    _syncWorld() {
        this.clamp();
        this.world.style.transform = `translate(${this.panX}px,${this.panY}px) scale(${this.zoom})`;
        // Mark the viewport playable once zoom crosses the lightbox threshold —
        // CSS uses this to gate the PLAY hover overlay so it only appears when
        // a click would actually open the video.
        this.viewport.classList.toggle('is-playable', this.zoom >= ZOOM_LIGHTBOX);
    }

    clamp() {
        const min = this.W < 768 ? 60 : 400;
        const ww  = this.worldW * this.zoom;
        const wh  = this.worldH * this.zoom;
        this.panX = Math.max(min - ww, Math.min(this.W - min, this.panX));
        this.panY = Math.max(min - wh, Math.min(this.H - min, this.panY));
    }

    applyInertia() {
        if (this._dragging) return;
        this.panX += this.velX * 16;
        this.panY += this.velY * 16;
        this.velX *= 0.88; this.velY *= 0.88;
        if (Math.abs(this.velX) < 0.001) this.velX = 0;
        if (Math.abs(this.velY) < 0.001) this.velY = 0;

        if (Math.abs(this.zoomVel) > 0.0005) {
            this.zoomAt(this.zoomCX, this.zoomCY, Math.exp(this.zoomVel));
            this.zoomVel *= 0.78;
        } else {
            this.zoomVel = 0;
        }

        this._syncWorld();
        this._maybeIdle();
    }

    zoomIn() {
        const next = this.ZOOM_LEVELS.find(z => z > this.zoom + 0.02);
        if (next) this.animateZoom(next, this.W / 2, this.H / 2);
    }

    zoomOut() {
        const prev = [...this.ZOOM_LEVELS].reverse().find(z => z < this.zoom - 0.02);
        this.animateZoom(prev ?? this.ZOOM_LEVELS[0], this.W / 2, this.H / 2);
    }

    animateZoom(targetZ, cx, cy) {
        this.zoomVel = 0;
        this._wake();
        const startZ = this.zoom, startTime = performance.now();
        const tick = now => {
            const t     = Math.min(1, (now - startTime) / 300);
            const eased = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
            this.zoomAt(cx, cy, (startZ + (targetZ - startZ) * eased) / this.zoom);
            if (t < 1) requestAnimationFrame(tick);
            else this._maybeIdle();
        };
        requestAnimationFrame(tick);
    }

    // ── Click / modal ─────────────────────────────────────────────────────────

    handleClick(sx, sy) {
        const item = this.itemAt(sx, sy);
        if (!item) return;
        if (this.zoom >= ZOOM_LIGHTBOX) {
            this.openModal(item);
        } else {
            const screenCX = (item._x + ITEM_W / 2) * this.zoom + this.panX;
            const screenCY = (item._y + item._h / 2) * this.zoom + this.panY;
            this.animateZoom(this.zoom * 2.5, screenCX, screenCY);
        }
    }

    itemAt(sx, sy) {
        const wx = (sx - this.panX) / this.zoom;
        const wy = (sy - this.panY) / this.zoom;
        for (const item of this.items) {
            if (wx >= item._x && wx <= item._x + ITEM_W &&
                wy >= item._y && wy <= item._y + item._h) return item;
        }
        return null;
    }

    checkUrlParam() {
        const param = new URLSearchParams(window.location.search).get('item');
        if (!param) return;
        const item = this.data.find(i => i.id === param);
        if (item) requestAnimationFrame(() => this.openModal(item));
    }

    openModal(item) {
        if (!item.embed_url) return;
        // muted=1: autoplay silently — the viewer unmutes via the player UI.
        this.modalIframe.src = item.embed_url + '?autoplay=1&muted=1&dnt=1';
        if (this.modalTitle) this.modalTitle.textContent = item.title || '';
        this.updateCrumb(item);
        this.modal.classList.add('is-open');
        document.body.classList.add('modal-open');
        const shareUrl = `${location.origin}${location.pathname}?item=${item.id}`;
        showShare(item, shareUrl);
    }

    closeModal() {
        this.modal.classList.remove('is-open');
        document.body.classList.remove('modal-open');
        this.modalIframe.src = '';
        this.updateCrumb(this._crumbItem);
        hideShare();
    }

    // ── Centre / crumb ────────────────────────────────────────────────────────

    _centerItem() {
        const cx = (this.W / 2 - this.panX) / this.zoom;
        const cy = (this.H / 2 - this.panY) / this.zoom;
        let best = null, bestDist = Infinity;
        for (const item of this.items) {
            if (!this._itemVisible(item)) continue;
            const dx = (item._x + ITEM_W / 2) - cx;
            const dy = (item._y + item._h / 2) - cy;
            const d  = dx * dx + dy * dy;
            if (d < bestDist) { bestDist = d; best = item; }
        }
        return best;
    }

    updateCrumb(item) {
        const el = document.getElementById('video-crumb');
        if (!el) return;
    
        // Separator spacing: see the note in gallery-animate-zoom.js updateCrumb.
        // The &nbsp; must be the only space, so no whitespace between tags.
        const sep  = '<span class="hidden md:inline">&nbsp;/ </span>';
        const part = text => `<span class="block md:inline">${text}</span>`;

        let html = '';

        if (this._filterCrumb) html += sep + part(this._filterCrumb);

        if (item?.title) {
            const parts = item.title.split(' / ');
            const title = parts.length > 1
                ? parts.slice(1).join(' / ')
                : item.title;

            html += sep + part(title);
        }

        el.innerHTML = html;
    }
}

export function init() {
    // Always destroy the previous instance so its RAF stops even when navigating
    // to a page without the video wall (swup keeps JS alive).
    window.JK.videoWall?.destroy();
    window.JK.videoWall = null;
    if (document.getElementById('video-viewport')) {
        window.JK.videoWall = new VideoWall();
    }
}
